import React, { useState, useRef, Component } from 'react';
import { ClipboardList, TrendingUp, Globe, Lightbulb, Users, DollarSign, Briefcase, Lock, AlertCircle, CheckCircle2, Download } from 'lucide-react';
import * as XLSX from 'xlsx';
const SURVEY_FIELDS = [{
  key: 'productivity_support',
  section: 'Business Growth & Productivity',
  label: 'Support received to improve productivity or operations'
}, {
  key: 'productivity_spending',
  section: 'Business Growth & Productivity',
  label: 'Project spending toward productivity improvements'
}, {
  key: 'new_export_countries',
  section: 'Market Expansion & Sales',
  label: 'New countries exported to during program'
}, {
  key: 'new_products_services',
  section: 'Market Expansion & Sales',
  label: 'New products, services, or business processes launched'
}, {
  key: 'sales_value',
  section: 'Market Expansion & Sales',
  label: 'Estimated value of sales from new products/services'
}, {
  key: 'intellectual_property',
  section: 'Innovation & Development',
  label: 'New intellectual property created or licensed'
}, {
  key: 'prototypes_count',
  section: 'Innovation & Development',
  label: 'Prototypes, pilots, or demo-ready solutions developed'
}, {
  key: 'rd_spending',
  section: 'Innovation & Development',
  label: 'Project spending toward R&D'
}, {
  key: 'people_trained',
  section: 'Training & Community Impact',
  label: 'People who received training or mentorship'
}, {
  key: 'outside_investment',
  section: 'Investment & Funding',
  label: 'Outside investment attracted during program'
}, {
  key: 'investment_amount',
  section: 'Investment & Funding',
  label: 'Approximate investment amount'
}, {
  key: 'new_perm_prof',
  section: 'Job Creation — New Roles',
  label: 'New permanent professional/technical roles'
}, {
  key: 'new_perm_nonprof',
  section: 'Job Creation — New Roles',
  label: 'New permanent non-professional roles'
}, {
  key: 'new_temp_prof',
  section: 'Job Creation — New Roles',
  label: 'New temporary professional/technical roles'
}, {
  key: 'new_temp_nonprof',
  section: 'Job Creation — New Roles',
  label: 'New temporary non-professional roles'
}, {
  key: 'maint_perm_prof',
  section: 'Job Creation — Maintained Roles',
  label: 'Maintained permanent professional/technical roles'
}, {
  key: 'maint_perm_nonprof',
  section: 'Job Creation — Maintained Roles',
  label: 'Maintained permanent non-professional roles'
}, {
  key: 'maint_temp_prof',
  section: 'Job Creation — Maintained Roles',
  label: 'Maintained temporary professional/technical roles'
}, {
  key: 'maint_temp_nonprof',
  section: 'Job Creation — Maintained Roles',
  label: 'Maintained temporary non-professional roles'
}];
export function SurveyPage() {
  const [submitted, setSubmitted] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);
  const collectFormData = () => {
    if (!formRef.current) return {};
    const formData = new FormData(formRef.current);
    const data: Record<string, string> = {};
    SURVEY_FIELDS.forEach(field => {
      data[field.key] = formData.get(field.key) as string || '';
    });
    return data;
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
  };
  const handleDownloadExcel = () => {
    const data = collectFormData();
    const rows = SURVEY_FIELDS.map(field => ({
      Section: field.section,
      Question: field.label,
      Response: data[field.key] || ''
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    // Set column widths
    ws['!cols'] = [{
      wch: 35
    }, {
      wch: 55
    }, {
      wch: 40
    }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Mid-Program Survey');
    // Add metadata sheet
    const metaRows = [{
      Field: 'Survey',
      Value: 'Mid-Program Progress Questionnaire'
    }, {
      Field: 'Program',
      Value: 'BOF Catalyst — BFN Scale Program'
    }, {
      Field: 'Respondent',
      Value: 'Sarah Jenkins'
    }, {
      Field: 'Company',
      Value: 'Spark Station'
    }, {
      Field: 'Cohort',
      Value: 'Cohort 1'
    }, {
      Field: 'Date Exported',
      Value: new Date().toLocaleDateString('en-CA')
    }];
    const metaWs = XLSX.utils.json_to_sheet(metaRows);
    metaWs['!cols'] = [{
      wch: 20
    }, {
      wch: 50
    }];
    XLSX.utils.book_append_sheet(wb, metaWs, 'Metadata');
    XLSX.writeFile(wb, `BOF_Catalyst_MidProgram_Survey_${new Date().toISOString().split('T')[0]}.xlsx`);
  };
  if (submitted) {
    return <div className="max-w-3xl mx-auto py-20 text-center animate-in fade-in duration-500" data-id="element-667">
        <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6" data-id="element-668">
          <CheckCircle2 size={40} className="text-green-600" data-id="element-669" />
        </div>
        <h1 className="text-3xl font-heading font-bold text-catalyst-black mb-3" data-id="element-670">
          Survey Submitted
        </h1>
        <p className="text-gray-500 max-w-md mx-auto mb-8" data-id="element-671">
          Thank you for completing the Mid-Program Progress Questionnaire. Your
          insights help us better support entrepreneurs and measure meaningful
          business growth.
        </p>
        <button onClick={handleDownloadExcel} className="inline-flex items-center gap-2 bg-catalyst-mustard text-catalyst-black px-8 py-3 rounded-xl text-base font-bold hover:bg-catalyst-mustard-light transition-all shadow-lg shadow-catalyst-mustard/20" data-id="element-672">
          <Download size={20} data-id="element-673" />
          Download Results (Excel)
        </button>
      </div>;
  }
  return <div className="max-w-3xl mx-auto space-y-8 animate-in fade-in duration-500 pb-12" data-id="element-674">
      {/* Header */}
      <div className="border-b border-gray-200 pb-6" data-id="element-675">
        <div className="flex items-center justify-between mb-3" data-id="element-676">
          <div className="flex items-center gap-3" data-id="element-677">
            <div className="p-2 rounded-lg bg-catalyst-mustard/15" data-id="element-678">
              <ClipboardList size={22} className="text-catalyst-mustard-dark" data-id="element-679" />
            </div>
            <span className="text-xs font-bold text-catalyst-mustard-dark uppercase tracking-widest bg-catalyst-mustard/10 px-3 py-1 rounded-full border border-catalyst-mustard/20" data-id="element-680">
              Mandatory
            </span>
          </div>
          <button onClick={handleDownloadExcel} className="inline-flex items-center gap-2 bg-white border border-gray-200 text-gray-700 px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-50 hover:border-gray-300 transition-colors shadow-sm" data-id="element-681">
            <Download size={16} data-id="element-682" />
            Export to Excel
          </button>
        </div>
        <h1 className="text-3xl font-heading font-bold text-catalyst-black tracking-tight" data-id="element-683">
          Mid-Program Progress Questionnaire
        </h1>
        <p className="text-gray-500 mt-2 leading-relaxed" data-id="element-684">
          Please complete this questionnaire midway through your 6-month
          program. Your responses help us understand your business growth,
          progress, and the impact of the support you've received so far.
        </p>
        <div className="mt-4 bg-catalyst-mustard/10 border border-catalyst-mustard/20 rounded-xl p-4 flex items-start gap-3" data-id="element-685">
          <AlertCircle size={18} className="text-catalyst-mustard-dark shrink-0 mt-0.5" data-id="element-686" />
          <p className="text-sm text-catalyst-brown-dark font-medium" data-id="element-687">
            This survey is mandatory for all program participants. Please answer
            as accurately as possible.
          </p>
        </div>
      </div>

      <form ref={formRef} onSubmit={handleSubmit} className="space-y-8" data-id="element-688">
        {/* Section 1: Business Growth & Productivity */}
        <SurveySection icon={TrendingUp} title="Business Growth & Productivity" number={1} data-id="element-689">
          <SurveyTextarea name="productivity_support" label="Has your business received support that helped improve productivity or operations?" placeholder="Describe any support received and how it impacted your business operations..." data-id="element-690" />
          <SurveyTextarea name="productivity_spending" label="Approximately how much of your project spending so far has gone toward productivity improvements?" sublabel="Such as systems, tools, team support, operations, or process improvements" placeholder="e.g. 30% of total project spending, approximately $15,000..." data-id="element-691" />
        </SurveySection>

        {/* Section 2: Market Expansion & Sales */}
        <SurveySection icon={Globe} title="Market Expansion & Sales" number={2} data-id="element-692">
          <SurveyTextarea name="new_export_countries" label="Has your business started exporting to any new countries during this program?" placeholder="If yes, please list the countries..." data-id="element-693" />
          <SurveyTextarea name="new_products_services" label="Have you launched any new products, services, or business processes during the program?" placeholder="Please describe them..." data-id="element-694" />
          <SurveyInput name="sales_value" label="What is the estimated value of sales generated from these new products, services, or processes so far?" placeholder="e.g. $50,000" prefix="$" data-id="element-695" />
        </SurveySection>

        {/* Section 3: Innovation & Development */}
        <SurveySection icon={Lightbulb} title="Innovation & Development" number={3} data-id="element-696">
          <SurveyTextarea name="intellectual_property" label="Have you created or licensed any new intellectual property?" sublabel="Such as trademarks, patents, copyrights, proprietary frameworks, or licensing agreements" placeholder="Please describe..." data-id="element-697" />
          <SurveyInput name="prototypes_count" label="How many prototypes, pilot programs, or demonstration-ready solutions have you developed so far?" placeholder="e.g. 2" type="number" data-id="element-698" />
          <SurveyTextarea name="rd_spending" label="Approximately how much of your project spending has gone toward research and development (R&D)?" placeholder="e.g. 40% of total project spending, approximately $25,000..." data-id="element-699" />
        </SurveySection>

        {/* Section 4: Training, Mentorship & Community Impact */}
        <SurveySection icon={Users} title="Training, Mentorship & Community Impact" number={4} data-id="element-700">
          <SurveyInput name="people_trained" label="How many people have received training, mentorship, or professional development through your business during this program?" sublabel="Please do not include your customers or service recipients — only those directly trained or mentored." placeholder="e.g. 5" type="number" data-id="element-701" />
        </SurveySection>

        {/* Section 5: Investment & Funding */}
        <SurveySection icon={DollarSign} title="Investment & Funding" number={5} data-id="element-702">
          <SurveyTextarea name="outside_investment" label="Has your business attracted any outside investment during the program?" sublabel="For example: investors, grants, sponsorships, strategic partnerships, or foreign direct investment" placeholder="Describe any investment or funding received..." data-id="element-703" />
          <SurveyInput name="investment_amount" label="If yes, what is the approximate amount?" placeholder="e.g. $100,000" prefix="$" data-id="element-704" />
        </SurveySection>

        {/* Section 6: Job Creation & Team Growth */}
        <SurveySection icon={Briefcase} title="Job Creation & Team Growth" number={6} data-id="element-705">
          <div className="space-y-2 mb-4" data-id="element-706">
            <h4 className="text-sm font-bold text-catalyst-black" data-id="element-707">
              New Roles Created
            </h4>
            <p className="text-xs text-gray-500" data-id="element-708">As a result of this program</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-709">
            <SurveyInput name="new_perm_prof" label="Permanent professional / technical roles" placeholder="0" type="number" compact data-id="element-710" />
            <SurveyInput name="new_perm_nonprof" label="Permanent non-professional roles" placeholder="0" type="number" compact data-id="element-711" />
            <SurveyInput name="new_temp_prof" label="Temporary professional / technical roles" placeholder="0" type="number" compact data-id="element-712" />
            <SurveyInput name="new_temp_nonprof" label="Temporary non-professional roles" placeholder="0" type="number" compact data-id="element-713" />
          </div>

          <div className="border-t border-gray-100 pt-6 mt-6" data-id="element-714">
            <div className="space-y-2 mb-4" data-id="element-715">
              <h4 className="text-sm font-bold text-catalyst-black" data-id="element-716">
                Existing Roles Maintained
              </h4>
              <p className="text-xs text-gray-500" data-id="element-717">Because of this program</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-718">
              <SurveyInput name="maint_perm_prof" label="Permanent professional / technical roles" placeholder="0" type="number" compact data-id="element-719" />
              <SurveyInput name="maint_perm_nonprof" label="Permanent non-professional roles" placeholder="0" type="number" compact data-id="element-720" />
              <SurveyInput name="maint_temp_prof" label="Temporary professional / technical roles" placeholder="0" type="number" compact data-id="element-721" />
              <SurveyInput name="maint_temp_nonprof" label="Temporary non-professional roles" placeholder="0" type="number" compact data-id="element-722" />
            </div>
          </div>
        </SurveySection>

        {/* Section 7: Looking Ahead (Locked) */}
        <div className="bg-gray-50 rounded-2xl border border-gray-200 overflow-hidden opacity-60" data-id="element-723">
          <div className="px-6 py-5 border-b border-gray-200 flex items-center justify-between" data-id="element-724">
            <div className="flex items-center gap-3" data-id="element-725">
              <div className="p-2 rounded-lg bg-gray-200" data-id="element-726">
                <Lock size={20} className="text-gray-500" data-id="element-727" />
              </div>
              <div data-id="element-728">
                <h3 className="font-bold text-gray-600" data-id="element-729">Looking Ahead</h3>
                <p className="text-xs text-gray-500" data-id="element-730">
                  To be completed at program end
                </p>
              </div>
            </div>
            <span className="text-xs font-bold text-gray-500 bg-gray-200 px-3 py-1 rounded-full" data-id="element-731">
              Locked
            </span>
          </div>
          <div className="px-6 py-5 space-y-4" data-id="element-732">
            <p className="text-sm text-gray-500" data-id="element-733">
              The following questions will be completed at the end of the full
              6-month program:
            </p>
            <ul className="text-sm text-gray-500 space-y-2 list-disc pl-5" data-id="element-734">
              <li data-id="element-735">
                What is your company's total annual revenue at the end of the
                program?
              </li>
              <li data-id="element-736">
                What is your total export revenue within North America at the
                end of the program?
              </li>
              <li data-id="element-737">
                What is your total export revenue outside North America at the
                end of the program?
              </li>
            </ul>
          </div>
        </div>

        {/* Submit & Export */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6" data-id="element-738">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4" data-id="element-739">
            <p className="text-sm text-gray-500" data-id="element-740">
              All responses are confidential and used to measure program impact.
            </p>
            <div className="flex items-center gap-3 shrink-0" data-id="element-741">
              <button type="button" onClick={handleDownloadExcel} className="inline-flex items-center gap-2 bg-white border border-gray-300 text-gray-700 px-5 py-3 rounded-xl text-sm font-semibold hover:bg-gray-50 transition-colors shadow-sm" data-id="element-742">
                <Download size={18} data-id="element-743" />
                Export Excel
              </button>
              <button type="submit" className="bg-catalyst-mustard text-catalyst-black px-10 py-3 rounded-xl text-base font-bold hover:bg-catalyst-mustard-light transition-all shadow-lg shadow-catalyst-mustard/20 hover:shadow-xl hover:shadow-catalyst-mustard/30" data-id="element-744">
                Submit Survey
              </button>
            </div>
          </div>
        </div>
      </form>
    </div>;
}
/* ---- Reusable Sub-Components ---- */
function SurveySection({
  icon: Icon,
  title,
  number,
  children
}: {
  icon: React.ElementType;
  title: string;
  number: number;
  children: React.ReactNode;
}) {
  return <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden" data-id="element-745">
      <div className="px-6 py-5 border-b border-gray-100 flex items-center gap-3" data-id="element-746">
        <div className="p-2 rounded-lg bg-catalyst-mustard/10" data-id="element-747">
          <Icon size={20} className="text-catalyst-mustard-dark" data-id="element-748" />
        </div>
        <div data-id="element-749">
          <span className="text-[10px] font-bold text-catalyst-mustard-dark uppercase tracking-wider" data-id="element-750">
            Section {number}
          </span>
          <h3 className="font-bold text-catalyst-black" data-id="element-751">{title}</h3>
        </div>
      </div>
      <div className="p-6 space-y-6" data-id="element-752">{children}</div>
    </div>;
}
function SurveyTextarea({
  name,
  label,
  sublabel,
  placeholder
}: {
  name: string;
  label: string;
  sublabel?: string;
  placeholder: string;
}) {
  return <div data-id="element-753">
      <label className="block text-sm font-semibold text-catalyst-black mb-1" data-id="element-754">
        {label}
      </label>
      {sublabel && <p className="text-xs text-gray-500 mb-2" data-id="element-755">{sublabel}</p>}
      <textarea name={name} className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-catalyst-mustard focus:border-catalyst-mustard text-sm min-h-[100px] resize-y transition-colors" placeholder={placeholder} data-id="element-756" />
    </div>;
}
function SurveyInput({
  name,
  label,
  sublabel,
  placeholder,
  type = 'text',
  prefix,
  compact
}: {
  name: string;
  label: string;
  sublabel?: string;
  placeholder: string;
  type?: string;
  prefix?: string;
  compact?: boolean;
}) {
  return <div data-id="element-757">
      <label className={`block font-semibold text-catalyst-black mb-1 ${compact ? 'text-xs' : 'text-sm'}`} data-id="element-758">
        {label}
      </label>
      {sublabel && <p className="text-xs text-gray-500 mb-2" data-id="element-759">{sublabel}</p>}
      <div className="relative" data-id="element-760">
        {prefix && <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm" data-id="element-761">
            {prefix}
          </span>}
        <input name={name} type={type} className={`w-full ${prefix ? 'pl-8' : 'pl-4'} pr-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-catalyst-mustard focus:border-catalyst-mustard text-sm transition-colors`} placeholder={placeholder} data-id="element-762" />
      </div>
    </div>;
}